import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@clerk/clerk-react";
import { GlobalNavigation } from "@/components/global-navigation";
import { HeroSection } from "@/components/landing/hero-section";
import { DifferentiatorsSection } from "@/components/landing/differentiators-section";
import { FounderSection } from "@/components/landing/founder-section";
import { SuccessVideoSection } from "@/components/landing/success-video-section";
import { EmbeddedConsultingSection } from "@/components/landing/embedded-consulting-section";
import { StepsSection } from "@/components/landing/steps-section";
import { ReadyCtaSection } from "@/components/landing/ready-cta-section";
import { LandingFooter } from "@/components/landing/landing-footer";
import { useProfile } from "@/lib/hooks/use-profile";
import { useIsAdmin } from "@/lib/hooks/use-is-admin";
import { SEOHead } from "@/components/seo/SEOHead";

export default function LandingPage() {
  const { isSignedIn } = useAuth();
  const navigate = useNavigate();
  const { profile, isLoading } = useProfile();
  const isAdmin = useIsAdmin();

  useEffect(() => {
    if (isSignedIn && !isLoading && profile) {
      // Check if onboarding is complete first
      if (!profile.onboarding_completed) {
        navigate("/onboarding", { replace: true });
        return;
      }

      // Navigate based on user's role to their dashboard
      if (isAdmin) {
        navigate("/admin", { replace: true });
      } else if (profile.is_genius) {
        navigate("/genius/dashboard", { replace: true });
      } else if (profile.is_client) {
        navigate("/client/dashboard", { replace: true });
      } else {
        // If no role is set, they need onboarding
        navigate("/onboarding", { replace: true });
      }
    }
  }, [isSignedIn, navigate, profile, isLoading, isAdmin]);

  return (
    <div className="flex min-h-screen flex-col bg-slate-50 text-slate-900">
      <SEOHead
        title="Hire Top AI Experts"
        description="Connect with vetted AI consultants, engineers, and specialists. AI Expert Network matches businesses with top AI talent for your projects."
        canonicalUrl="https://aiexpertnetwork.com"
        ogType="website"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'Organization',
          name: 'AI Expert Network',
          url: 'https://aiexpertnetwork.com',
          logo: 'https://aiexpertnetwork.com/logo.png',
          description: 'We nurture AI consulting excellence through continuous training and community.',
        }}
      />
      <GlobalNavigation />
      <main className="flex-1">
        <HeroSection />
        <DifferentiatorsSection />
        <FounderSection />
        <SuccessVideoSection />
        <EmbeddedConsultingSection />
        <StepsSection />
        <ReadyCtaSection />
      </main>
      <LandingFooter />
    </div>
  );
}
